import { useQuery, usePaginatedQuery } from "convex/react";
import { api } from "../../../convex/_generated/api";
import { Id } from "../../../convex/_generated/dataModel";
import { FC, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { atlasRoutes } from "@/app/router/routes";
import {
  Tabs,
  Table,
  Flex,
  Heading,
  Text,
  Badge,
  Button,
  Card,
} from "@radix-ui/themes";
import { StatusBadge } from "./components/status-badge";
import { RelativeTime, formatDuration } from "./components/relative-time";
import { JsonInspector } from "./components/json-inspector";
const TABS = [
  "timeline",
  "runs",
  "signals",
  "threads",
  "tasks",
  "reminders",
  "objectives",
  "schedules",
  "messages",
] as const;

type TabKey = (typeof TABS)[number];

export const MembotSessionDetailPage: FC = () => {
  const { sessionId, tab } = useParams<{ sessionId: string; tab: string }>();
  const navigate = useNavigate();
  const activeTab: TabKey = TABS.includes(tab as TabKey)
    ? (tab as TabKey)
    : "timeline";

  const session = useQuery(
    api.atlas.getSessionDetail,
    sessionId ? { sessionId } : "skip"
  );
  const stats = useQuery(
    api.atlas.getSessionStats,
    sessionId && session?.spaceAccountId
      ? { sessionId, spaceAccountId: session.spaceAccountId }
      : "skip"
  );

  if (!sessionId) return <Text>Missing session ID</Text>;

  const handleTabChange = (value: string) => {
    if (value === "timeline") {
      navigate(
        atlasRoutes.membotView({ params: { sessionId } }).path
      );
    } else {
      navigate(
        atlasRoutes.membotViewTab({
          params: { sessionId, tab: value },
        }).path
      );
    }
  };

  return (
    <div>
      <Flex justify="between" align="center" mb="4">
        <Flex direction="column" gap="1">
          <Flex align="center" gap="2">
            <Button
              variant="ghost"
              size="1"
              onClick={() => navigate(atlasRoutes.membot({}).path)}
            >
              &larr; Sessions
            </Button>
          </Flex>
          <Heading size="5">
            {session?.slackUserName ?? sessionId.slice(0, 16) + "..."}
          </Heading>
          {session && (
            <Flex gap="2" align="center">
              <Text size="1" color="gray" style={{ fontFamily: "monospace" }}>
                {session.sessionId}
              </Text>
              {session.slackUserEmail && (
                <Text size="1" color="gray">
                  ({session.slackUserEmail})
                </Text>
              )}
              {session.assistantStatus && (
                <StatusBadge status={session.assistantStatus} />
              )}
            </Flex>
          )}
        </Flex>
        {stats && (
          <Flex gap="3">
            <StatCard label="Runs" value={stats.runs} />
            <StatCard label="Threads" value={stats.threads} />
            <StatCard label="Tasks" value={stats.tasks} />
            <StatCard label="Reminders" value={stats.reminders} />
            <StatCard label="Objectives" value={stats.objectives} />
          </Flex>
        )}
      </Flex>

      <Tabs.Root value={activeTab} onValueChange={handleTabChange}>
        <Tabs.List>
          <Tabs.Trigger value="timeline">Timeline</Tabs.Trigger>
          <Tabs.Trigger value="runs">Runs</Tabs.Trigger>
          <Tabs.Trigger value="signals">Signals</Tabs.Trigger>
          <Tabs.Trigger value="threads">Threads</Tabs.Trigger>
          <Tabs.Trigger value="tasks">Tasks</Tabs.Trigger>
          <Tabs.Trigger value="reminders">Reminders</Tabs.Trigger>
          <Tabs.Trigger value="objectives">Objectives</Tabs.Trigger>
          <Tabs.Trigger value="schedules">Schedules</Tabs.Trigger>
          <Tabs.Trigger value="messages">Messages</Tabs.Trigger>
        </Tabs.List>

        <Tabs.Content value="timeline">
          <TimelineTab sessionId={sessionId} />
        </Tabs.Content>
        <Tabs.Content value="runs">
          <RunsTab sessionId={sessionId} />
        </Tabs.Content>
        <Tabs.Content value="signals">
          {session?.assistantId && (
            <SignalsTab assistantId={session.assistantId} sessionId={sessionId} />
          )}
        </Tabs.Content>
        <Tabs.Content value="threads">
          {session?.spaceAccountId && (
            <ThreadsTab spaceAccountId={session.spaceAccountId} />
          )}
        </Tabs.Content>
        <Tabs.Content value="tasks">
          {session?.spaceAccountId && (
            <TasksTab spaceAccountId={session.spaceAccountId} />
          )}
        </Tabs.Content>
        <Tabs.Content value="reminders">
          {session?.spaceAccountId && (
            <RemindersTab spaceAccountId={session.spaceAccountId} />
          )}
        </Tabs.Content>
        <Tabs.Content value="objectives">
          {session?.spaceAccountId && (
            <ObjectivesTab spaceAccountId={session.spaceAccountId} />
          )}
        </Tabs.Content>
        <Tabs.Content value="schedules">
          {session?.spaceAccountId && (
            <SchedulesTab spaceAccountId={session.spaceAccountId} />
          )}
        </Tabs.Content>
        <Tabs.Content value="messages">
          <MessagesTab sessionId={sessionId} />
        </Tabs.Content>
      </Tabs.Root>
    </div>
  );
};

// ── Stat Card ──────────────────────────────────────────────────────

const StatCard: FC<{ label: string; value: number }> = ({ label, value }) => (
  <Card size="1">
    <Flex direction="column" align="center" gap="1">
      <Text size="4" weight="bold">
        {value}
      </Text>
      <Text size="1" color="gray">
        {label}
      </Text>
    </Flex>
  </Card>
);

// ── Timeline Tab ────────────────────────────────────────────────────

interface TimelineEntry {
  id: string;
  kind: string;
  timestamp: number;
  summary: string;
  detail?: Record<string, unknown>;
}

const PAGE_SIZE = 50;

const TimelineTab: FC<{ sessionId: string }> = ({ sessionId }) => {
  const {
    results: runs,
    status: runsStatus,
    loadMore: loadMoreRuns,
  } = usePaginatedQuery(
    api.atlas.paginateRunsBySession,
    { sessionId },
    { initialNumItems: PAGE_SIZE }
  );
  const {
    results: events,
    status: eventsStatus,
    loadMore: loadMoreEvents,
  } = usePaginatedQuery(
    api.atlas.paginateEventsBySession,
    { sessionId },
    { initialNumItems: PAGE_SIZE }
  );

  const entries: TimelineEntry[] = useMemo(() => {
    const runEntries: TimelineEntry[] = runs.map((r) => ({
      id: r._id,
      kind: `run_${r.status}`,
      timestamp: r.startedAt,
      summary: `${r.model ?? "?"} | ${r.signalIds.length} signals | ${formatDuration(r.startedAt, r.completedAt ?? undefined)}`,
      detail: { workflowId: r.workflowId, error: r.error },
    }));
    const eventEntries: TimelineEntry[] = events.map((e) => ({
      id: e._id,
      kind: e.kind,
      timestamp: e.createdAt,
      summary: e.message,
      detail: e.sourceKind ? { sourceKind: e.sourceKind } : undefined,
    }));
    return [...runEntries, ...eventEntries].sort(
      (a, b) => b.timestamp - a.timestamp
    );
  }, [runs, events]);

  const isLoading =
    runsStatus === "LoadingFirstPage" || eventsStatus === "LoadingFirstPage";
  const canLoadMore =
    runsStatus === "CanLoadMore" || eventsStatus === "CanLoadMore";

  if (isLoading) {
    return <Text color="gray">Loading timeline...</Text>;
  }

  if (entries.length === 0) {
    return (
      <Text color="gray" mt="3">
        No timeline events yet.
      </Text>
    );
  }

  return (
    <div>
      <Table.Root variant="surface" mt="3">
        <Table.Header>
          <Table.Row>
            <Table.ColumnHeaderCell>Time</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>Kind</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>Summary</Table.ColumnHeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {entries.map((entry) => (
            <Table.Row key={entry.id}>
              <Table.Cell>
                <RelativeTime timestamp={entry.timestamp} />
              </Table.Cell>
              <Table.Cell>
                <StatusBadge status={entry.kind} />
              </Table.Cell>
              <Table.Cell>
                <Text size="2">{entry.summary}</Text>
                {entry.detail && (
                  <div style={{ marginTop: 4 }}>
                    <JsonInspector data={entry.detail} label="Details" />
                  </div>
                )}
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table.Root>
      {canLoadMore && (
        <Flex justify="center" mt="3">
          <Button
            variant="soft"
            onClick={() => {
              if (runsStatus === "CanLoadMore") loadMoreRuns(PAGE_SIZE);
              if (eventsStatus === "CanLoadMore") loadMoreEvents(PAGE_SIZE);
            }}
          >
            Load more
          </Button>
        </Flex>
      )}
    </div>
  );
};

// ── Runs Tab ────────────────────────────────────────────────────────

const RunsTab: FC<{ sessionId: string }> = ({ sessionId }) => {
  const { results: runs, status, loadMore } = usePaginatedQuery(
    api.atlas.paginateRunsBySession,
    { sessionId },
    { initialNumItems: PAGE_SIZE }
  );
  const navigate = useNavigate();

  if (status === "LoadingFirstPage")
    return <Text color="gray">Loading runs...</Text>;

  return (
    <div>
      <Table.Root variant="surface" mt="3">
        <Table.Header>
          <Table.Row>
            <Table.ColumnHeaderCell>Status</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>Model</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>Workflow ID</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>Signals</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>Duration</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>Started</Table.ColumnHeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {runs.map((run) => (
            <Table.Row
              key={run._id}
              style={{ cursor: "pointer" }}
              onClick={() =>
                navigate(
                  atlasRoutes.membotRunsView({
                    params: { runId: run._id },
                  }).path
                )
              }
            >
              <Table.Cell>
                <StatusBadge status={run.status} />
              </Table.Cell>
              <Table.Cell>{run.model ?? "—"}</Table.Cell>
              <Table.Cell>
                <Text size="1" style={{ fontFamily: "monospace" }}>
                  {run.workflowId.slice(0, 12)}...
                </Text>
              </Table.Cell>
              <Table.Cell>
                <Badge variant="outline">{run.signalIds.length}</Badge>
              </Table.Cell>
              <Table.Cell>
                {formatDuration(run.startedAt, run.completedAt ?? undefined)}
              </Table.Cell>
              <Table.Cell>
                <RelativeTime timestamp={run.startedAt} />
              </Table.Cell>
            </Table.Row>
          ))}
          {runs.length === 0 && (
            <Table.Row>
              <Table.Cell colSpan={6}>
                <Text color="gray">No runs found.</Text>
              </Table.Cell>
            </Table.Row>
          )}
        </Table.Body>
      </Table.Root>
      {status === "CanLoadMore" && (
        <Flex justify="center" mt="3">
          <Button variant="soft" onClick={() => loadMore(PAGE_SIZE)}>
            Load more
          </Button>
        </Flex>
      )}
    </div>
  );
};

// ── Threads Tab ─────────────────────────────────────────────────────

const ThreadsTab: FC<{ spaceAccountId: string }> = ({ spaceAccountId }) => {
  const threads = useQuery(api.threads.listThreads, { spaceAccountId });
  const navigate = useNavigate();

  if (threads === undefined)
    return <Text color="gray">Loading threads...</Text>;

  return (
    <Table.Root variant="surface" mt="3">
      <Table.Header>
        <Table.Row>
          <Table.ColumnHeaderCell>Title</Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell>Status</Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell>Last Activity</Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell>Created</Table.ColumnHeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {threads.map((thread) => (
          <Table.Row
            key={thread._id}
            style={{ cursor: "pointer" }}
            onClick={() =>
              navigate(
                atlasRoutes.membotThreadsView({
                  params: { threadId: thread._id },
                }).path
              )
            }
          >
            <Table.Cell>
              <Text weight="medium">{thread.title}</Text>
            </Table.Cell>
            <Table.Cell>
              <StatusBadge status={thread.status} />
            </Table.Cell>
            <Table.Cell>
              <RelativeTime timestamp={thread.lastActivityAt} />
            </Table.Cell>
            <Table.Cell>
              <RelativeTime timestamp={thread.createdAt} />
            </Table.Cell>
          </Table.Row>
        ))}
        {threads.length === 0 && (
          <Table.Row>
            <Table.Cell colSpan={4}>
              <Text color="gray">No threads found.</Text>
            </Table.Cell>
          </Table.Row>
        )}
      </Table.Body>
    </Table.Root>
  );
};

// ── Tasks Tab ───────────────────────────────────────────────────────

const TasksTab: FC<{ spaceAccountId: string }> = ({ spaceAccountId }) => {
  const tasks = useQuery(api.threads.listTasks, { spaceAccountId });

  if (tasks === undefined) return <Text color="gray">Loading tasks...</Text>;

  return (
    <Table.Root variant="surface" mt="3">
      <Table.Header>
        <Table.Row>
          <Table.ColumnHeaderCell>Title</Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell>Status</Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell>Due</Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell>Created</Table.ColumnHeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {tasks.map((task) => (
          <Table.Row key={task._id}>
            <Table.Cell>
              <Text weight="medium">{task.title}</Text>
              {task.description && (
                <Text size="1" color="gray" as="div">
                  {task.description}
                </Text>
              )}
            </Table.Cell>
            <Table.Cell>
              <StatusBadge status={task.status} />
            </Table.Cell>
            <Table.Cell>
              {task.dueAt ? <RelativeTime timestamp={task.dueAt} /> : "—"}
            </Table.Cell>
            <Table.Cell>
              <RelativeTime timestamp={task.createdAt} />
            </Table.Cell>
          </Table.Row>
        ))}
        {tasks.length === 0 && (
          <Table.Row>
            <Table.Cell colSpan={4}>
              <Text color="gray">No tasks found.</Text>
            </Table.Cell>
          </Table.Row>
        )}
      </Table.Body>
    </Table.Root>
  );
};

// ── Reminders Tab ───────────────────────────────────────────────────

const RemindersTab: FC<{ spaceAccountId: string }> = ({ spaceAccountId }) => {
  const reminders = useQuery(api.threads.listReminders, { spaceAccountId });

  if (reminders === undefined)
    return <Text color="gray">Loading reminders...</Text>;

  return (
    <Table.Root variant="surface" mt="3">
      <Table.Header>
        <Table.Row>
          <Table.ColumnHeaderCell>Content</Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell>Status</Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell>Scheduled For</Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell>Created</Table.ColumnHeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {reminders.map((reminder) => (
          <Table.Row key={reminder._id}>
            <Table.Cell>
              <Text>{reminder.content}</Text>
            </Table.Cell>
            <Table.Cell>
              <StatusBadge status={reminder.status} />
            </Table.Cell>
            <Table.Cell>
              <RelativeTime timestamp={reminder.scheduledFor} />
            </Table.Cell>
            <Table.Cell>
              <RelativeTime timestamp={reminder.createdAt} />
            </Table.Cell>
          </Table.Row>
        ))}
        {reminders.length === 0 && (
          <Table.Row>
            <Table.Cell colSpan={4}>
              <Text color="gray">No reminders found.</Text>
            </Table.Cell>
          </Table.Row>
        )}
      </Table.Body>
    </Table.Root>
  );
};

// ── Objectives Tab ──────────────────────────────────────────────────

const ObjectivesTab: FC<{ spaceAccountId: string }> = ({ spaceAccountId }) => {
  const objectives = useQuery(api.objectives.listObjectives, {
    spaceAccountId,
  });

  if (objectives === undefined)
    return <Text color="gray">Loading objectives...</Text>;

  return (
    <Table.Root variant="surface" mt="3">
      <Table.Header>
        <Table.Row>
          <Table.ColumnHeaderCell>Title</Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell>Status</Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell>Priority</Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell>Key</Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell>Created</Table.ColumnHeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {objectives.map((obj) => (
          <Table.Row key={obj._id}>
            <Table.Cell>
              <Text weight="medium">{obj.title}</Text>
            </Table.Cell>
            <Table.Cell>
              <StatusBadge status={obj.status} />
            </Table.Cell>
            <Table.Cell>
              <StatusBadge status={obj.priority} />
            </Table.Cell>
            <Table.Cell>
              <Text size="1" style={{ fontFamily: "monospace" }}>
                {obj.key}
              </Text>
            </Table.Cell>
            <Table.Cell>
              <RelativeTime timestamp={obj.createdAt} />
            </Table.Cell>
          </Table.Row>
        ))}
        {objectives.length === 0 && (
          <Table.Row>
            <Table.Cell colSpan={5}>
              <Text color="gray">No objectives found.</Text>
            </Table.Cell>
          </Table.Row>
        )}
      </Table.Body>
    </Table.Root>
  );
};

// ── Schedules Tab ───────────────────────────────────────────────────

const SchedulesTab: FC<{ spaceAccountId: string }> = ({ spaceAccountId }) => {
  const schedules = useQuery(api.schedules.listSchedules, { spaceAccountId });

  if (schedules === undefined)
    return <Text color="gray">Loading schedules...</Text>;

  return (
    <Table.Root variant="surface" mt="3">
      <Table.Header>
        <Table.Row>
          <Table.ColumnHeaderCell>Label</Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell>Kind</Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell>Status</Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell>Description</Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell>Created</Table.ColumnHeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {schedules.map((schedule) => (
          <Table.Row key={schedule._id}>
            <Table.Cell>
              <Text weight="medium">{schedule.label}</Text>
            </Table.Cell>
            <Table.Cell>
              <Text size="1" style={{ fontFamily: "monospace" }}>
                {schedule.kind}
              </Text>
            </Table.Cell>
            <Table.Cell>
              <StatusBadge status={schedule.status} />
            </Table.Cell>
            <Table.Cell>
              <Text size="1" color="gray">
                {schedule.description}
              </Text>
            </Table.Cell>
            <Table.Cell>
              <RelativeTime timestamp={schedule.createdAt} />
            </Table.Cell>
          </Table.Row>
        ))}
        {schedules.length === 0 && (
          <Table.Row>
            <Table.Cell colSpan={5}>
              <Text color="gray">No schedules found.</Text>
            </Table.Cell>
          </Table.Row>
        )}
      </Table.Body>
    </Table.Root>
  );
};

// ── Signals Tab ─────────────────────────────────────────────────────

const SIGNAL_STATUSES = ["queued", "processing", "completed", "failed", "ignored"] as const;

const SignalsTab: FC<{ assistantId: Id<"assistants">; sessionId: string }> = ({
  assistantId,
  sessionId,
}) => {
  const signals = useQuery(api.atlas.listSignalsBySession, {
    assistantId,
    sessionId,
    limit: 200,
  });
  const [filterStatus, setFilterStatus] = useState<string | null>(null);

  if (signals === undefined)
    return <Text color="gray">Loading signals...</Text>;

  const filtered = filterStatus
    ? signals.filter((s) => s.status === filterStatus)
    : signals;

  return (
    <div>
      <Flex gap="2" mt="3" mb="3" wrap="wrap">
        <Button
          variant={filterStatus === null ? "solid" : "soft"}
          size="1"
          onClick={() => setFilterStatus(null)}
        >
          All ({signals.length})
        </Button>
        {SIGNAL_STATUSES.map((s) => {
          const count = signals.filter((sig) => sig.status === s).length;
          if (count === 0) return null;
          return (
            <Button
              key={s}
              variant={filterStatus === s ? "solid" : "soft"}
              size="1"
              onClick={() => setFilterStatus(s)}
            >
              {s} ({count})
            </Button>
          );
        })}
      </Flex>
      <Table.Root variant="surface">
        <Table.Header>
          <Table.Row>
            <Table.ColumnHeaderCell>Status</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>Source Kind</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>Dedupe Key</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>Payload</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>Created</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>Error</Table.ColumnHeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {filtered.map((signal) => (
            <Table.Row key={signal._id}>
              <Table.Cell>
                <StatusBadge status={signal.status} />
              </Table.Cell>
              <Table.Cell>
                <Text size="1" style={{ fontFamily: "monospace" }}>
                  {signal.sourceKind}
                </Text>
              </Table.Cell>
              <Table.Cell>
                <Text size="1" style={{ fontFamily: "monospace" }}>
                  {signal.dedupeKey.length > 30
                    ? signal.dedupeKey.slice(0, 30) + "..."
                    : signal.dedupeKey}
                </Text>
              </Table.Cell>
              <Table.Cell>
                {signal.payload ? (
                  <JsonInspector data={signal.payload} label="Payload" />
                ) : (
                  <Text size="1" color="gray">—</Text>
                )}
              </Table.Cell>
              <Table.Cell>
                <RelativeTime timestamp={signal.createdAt} />
              </Table.Cell>
              <Table.Cell>
                {signal.error ? (
                  <Text size="1" color="red">
                    {signal.error.length > 50
                      ? signal.error.slice(0, 50) + "..."
                      : signal.error}
                  </Text>
                ) : (
                  <Text size="1" color="gray">—</Text>
                )}
              </Table.Cell>
            </Table.Row>
          ))}
          {filtered.length === 0 && (
            <Table.Row>
              <Table.Cell colSpan={6}>
                <Text color="gray">No signals found.</Text>
              </Table.Cell>
            </Table.Row>
          )}
        </Table.Body>
      </Table.Root>
    </div>
  );
};

// ── Messages Tab ────────────────────────────────────────────────────

const MessagesTab: FC<{ sessionId: string }> = ({ sessionId }) => {
  const messages = useQuery(api.atlas.listSlackMessagesBySession, {
    sessionId,
    limit: 100,
  });

  if (messages === undefined)
    return <Text color="gray">Loading messages...</Text>;

  return (
    <Table.Root variant="surface" mt="3">
      <Table.Header>
        <Table.Row>
          <Table.ColumnHeaderCell>Sender</Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell>Text</Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell>Timestamp</Table.ColumnHeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {messages.map((msg) => (
          <Table.Row key={msg._id}>
            <Table.Cell>
              <StatusBadge status={msg.senderType} />
            </Table.Cell>
            <Table.Cell>
              <Text size="2" style={{ maxWidth: 600, display: "block" }}>
                {msg.text.length > 200 ? msg.text.slice(0, 200) + "..." : msg.text}
              </Text>
            </Table.Cell>
            <Table.Cell>
              <Text size="1" style={{ fontFamily: "monospace" }}>
                {msg.messageTs}
              </Text>
            </Table.Cell>
          </Table.Row>
        ))}
        {messages.length === 0 && (
          <Table.Row>
            <Table.Cell colSpan={3}>
              <Text color="gray">No messages found.</Text>
            </Table.Cell>
          </Table.Row>
        )}
      </Table.Body>
    </Table.Root>
  );
};
